import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router';
import {
    Box, Typography, InputAdornment, TextField, Chip, Divider,
    Paper, List, ListItemButton, ListItemIcon, ListItemText,
    Collapse, IconButton, Tooltip, useMediaQuery, Drawer,
    Fade, LinearProgress, Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import InventoryIcon from '@mui/icons-material/Inventory';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import SettingsIcon from '@mui/icons-material/Settings';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SchoolIcon from '@mui/icons-material/School';

// ── Tipos ──────────────────────────────────────────────────────────────────
interface Secao {
    id: string;
    label: string;
    icon: React.ReactNode;
    cor: string;
}

// ── Seções do menu lateral ─────────────────────────────────────────────────
const SECOES: Secao[] = [
    { id: 'empresa',     label: 'Empresa (Emitente)',    icon: <BusinessIcon />,       cor: '#1565c0' },
    { id: 'parceiro',    label: 'Parceiros',             icon: <PeopleIcon />,         cor: '#2e7d32' },
    { id: 'produto',     label: 'Produtos e Serviços',   icon: <InventoryIcon />,      cor: '#e65100' },
    { id: 'preco',       label: 'Gestão de Preços',      icon: <AttachMoneyIcon />,    cor: '#6a1b9a' },
    { id: 'menu',        label: 'Menu e Módulos',        icon: <MenuBookIcon />,       cor: '#00695c' },
    { id: 'inteligencia',label: 'Inteligência',          icon: <LightbulbIcon />,      cor: '#f57f17' },
    { id: 'agenda',      label: 'Configuração Agenda',   icon: <CalendarMonthIcon />,  cor: '#ad1457' },
    { id: 'delivery',    label: 'Configuração Delivery', icon: <DeliveryDiningIcon />, cor: '#4527a0' },
    { id: 'pdv-config',  label: 'Configuração PDV Easy', icon: <SettingsIcon />,              cor: '#37474f' },
    { id: 'pdv-venda',   label: 'Operando o PDV Easy',   icon: <PointOfSaleIcon />,           cor: '#1b5e20' },
    { id: 'nota',        label: 'Compra e Venda',         icon: <ReceiptIcon />,               cor: '#bf360c' },
    { id: 'financeiro',  label: 'Financeiro',             icon: <AccountBalanceWalletIcon />,  cor: '#00838f' },
    { id: 'estoque',     label: 'Estoque',                icon: <Inventory2Icon />,            cor: '#4e342e' },
    { id: 'fiscal',      label: 'Fiscal (NF-e / CT-e)',   icon: <ReceiptLongIcon />,           cor: '#1a237e' },
    { id: 'academia',    label: 'Academia / Escola',       icon: <SchoolIcon />,               cor: '#880e4f' },
];

// ── Componentes visuais auxiliares ─────────────────────────────────────────
const SecaoHeader: React.FC<{ secao: Secao }> = ({ secao }) => (
    <Box
        id={secao.id}
        sx={{
            display: 'flex', alignItems: 'center', gap: 2,
            mb: 3, pb: 2,
            borderBottom: `3px solid ${secao.cor}`,
            scrollMarginTop: '88px',
        }}
    >
        <Box
            sx={{
                width: 44, height: 44, borderRadius: '12px',
                bgcolor: secao.cor, color: 'white',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, fontSize: 22,
            }}
        >
            {secao.icon}
        </Box>
        <Typography variant="h5" fontWeight={800} color={secao.cor}>
            {secao.label}
        </Typography>
    </Box>
);

const Callout: React.FC<{
    tipo?: 'info' | 'aviso' | 'dica';
    titulo: string;
    children: React.ReactNode;
}> = ({ tipo = 'info', titulo, children }) => {
    const cfg = {
        info:  { cor: '#1565c0', bg: '#e3f2fd', icon: <InfoOutlinedIcon sx={{ color: '#1565c0' }} /> },
        aviso: { cor: '#e65100', bg: '#fff3e0', icon: <WarningAmberIcon sx={{ color: '#e65100' }} /> },
        dica:  { cor: '#2e7d32', bg: '#e8f5e9', icon: <CheckCircleIcon sx={{ color: '#2e7d32' }} /> },
    }[tipo];
    return (
        <Box sx={{ borderLeft: `4px solid ${cfg.cor}`, bgcolor: cfg.bg, borderRadius: '0 8px 8px 0', p: 2, my: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                {cfg.icon}
                <Typography variant="caption" fontWeight={700} color={cfg.cor} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {titulo}
                </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" component="div">{children}</Typography>
        </Box>
    );
};

const Passo: React.FC<{ num: number; titulo: string; children: React.ReactNode; ultimo?: boolean }> = ({ num, titulo, children, ultimo }) => (
    <Box sx={{ display: 'flex', gap: 2, mb: ultimo ? 0 : 2, position: 'relative' }}>
        {!ultimo && (
            <Box sx={{ position: 'absolute', left: 19, top: 44, width: 2, bottom: -8, bgcolor: 'divider' }} />
        )}
        <Box
            sx={{
                width: 40, height: 40, borderRadius: '50%',
                bgcolor: 'primary.main', color: 'white',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: 16, flexShrink: 0, zIndex: 1,
            }}
        >
            {num}
        </Box>
        <Box sx={{ pt: 1 }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>{titulo}</Typography>
            <Typography variant="body2" color="text.secondary" component="div">{children}</Typography>
        </Box>
    </Box>
);

const TagStatus: React.FC<{ label: string; cor?: string }> = ({ label, cor = '#1565c0' }) => (
    <Chip
        label={label}
        size="small"
        sx={{ bgcolor: `${cor}18`, color: cor, fontWeight: 600, fontSize: 11, height: 22, mx: 0.25 }}
    />
);

const TabelaSimples: React.FC<{ cabecalho: string[]; linhas: string[][] }> = ({ cabecalho, linhas }) => (
    <Box sx={{ overflowX: 'auto', my: 2 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
                <tr>
                    {cabecalho.map(h => (
                        <th key={h} style={{ background: '#1565c0', color: 'white', padding: '8px 14px', textAlign: 'left', fontWeight: 600 }}>
                            {h}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {linhas.map((linha, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? 'white' : '#f5f7fa' }}>
                        {linha.map((cel, j) => (
                            <td key={j} style={{ padding: '8px 14px', borderBottom: '1px solid #dde3ec', color: '#4a5568' }}>
                                {cel}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    </Box>
);

// ── Conteúdo das seções ────────────────────────────────────────────────────
const SecaoEmpresa: React.FC = () => (
    <Box>
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            <Typography variant="body2">
                <strong>Empresa ≠ Cliente ou Fornecedor.</strong> No Gestão, <strong>Empresa</strong> representa
                o próprio negócio gerenciado — é a pessoa jurídica <em>emitente</em> de notas fiscais,
                documentos e operações. Clientes e fornecedores são cadastrados como <strong>Parceiros</strong>.
            </Typography>
        </Alert>
        <Typography variant="body2" color="text.secondary" paragraph>
            Um tenant pode ter múltiplas empresas (filiais), cada uma com seu CNPJ, certificado digital
            e configurações independentes. Cada módulo operacional (PDV, Delivery, Agenda) exige
            que uma Empresa seja vinculada à sua configuração.
        </Typography>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mt: 2 }}>
            📍 Onde acessar
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
            Menu lateral → <strong>Cadastros</strong> → <strong>Empresa</strong>
        </Typography>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mt: 2 }}>
            📋 Como cadastrar
        </Typography>
        <Box>
            <Passo num={1} titulo="Identificação — importação do CNPJ">
                Informe o <strong>CNPJ</strong> e clique em <em>"Importar"</em>. O sistema consulta a
                Receita Federal e preenche Razão Social, Nome Fantasia e endereço automaticamente.
            </Passo>
            <Passo num={2} titulo="Dados Fiscais">
                Informe <strong>Inscrição Estadual</strong>, <strong>Inscrição Municipal</strong>
                (para NFS-e) e <strong>RNTRC</strong> se emite CT-e.
            </Passo>
            <Passo num={3} titulo="Endereço com CEP automático">
                Digite o <strong>CEP</strong> e pressione Tab. O endereço é preenchido via ViaCEP.
                Complete apenas o número e complemento.
            </Passo>
            <Passo num={4} titulo="Contato">
                Telefone, celular e e-mail — aparecem nas notas fiscais e documentos.
            </Passo>
            <Passo num={5} titulo="Logo" ultimo>
                Faça upload da logomarca. É usada em relatórios e documentos impressos.
            </Passo>
        </Box>
        <Callout tipo="dica" titulo="Dica">
            Cadastre a Empresa antes de configurar PDV, Delivery ou Agenda — todos os módulos
            precisam de uma Empresa vinculada.
        </Callout>
    </Box>
);

const SecaoParceiro: React.FC = () => (
    <Box>
        <Typography variant="body2" color="text.secondary" paragraph>
            Parceiro é qualquer pessoa física ou jurídica com quem a empresa se relaciona:
            <strong> clientes</strong>, <strong>fornecedores</strong>, <strong>transportadoras</strong>,
            <strong> representantes</strong>. O mesmo cadastro serve para todos os papéis —
            o papel é definido pelo tipo de operação (venda, compra, serviço).
        </Typography>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>📍 Onde acessar</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
            Menu lateral → <strong>Cadastros</strong> → <strong>Parceiro</strong>
        </Typography>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mt: 2 }}>📋 Como cadastrar</Typography>
        <Passo num={1} titulo="Tipo de Pessoa">
            Selecione <strong>Pessoa Física (F)</strong> ou <strong>Jurídica (J)</strong>.
            O sistema ajusta os campos: CPF ou CNPJ, RG ou IE.
        </Passo>
        <Passo num={2} titulo="Documento e importação">
            Para pessoa jurídica, informe o CNPJ e clique <em>"Importar SEFAZ"</em>
            para preencher dados automaticamente.
        </Passo>
        <Passo num={3} titulo="Nome / Razão Social">
            <strong>Razão Social</strong> (obrigatório), Nome Fantasia e Data de Nascimento
            (para pessoa física).
        </Passo>
        <Passo num={4} titulo="Endereço com CEP automático">
            CEP preenche endereço automaticamente. Complete apenas número e complemento.
        </Passo>
        <Passo num={5} titulo="Contato" ultimo>
            Celular pode ser usado para envio de mensagens via WhatsApp integrado.
        </Passo>
        <Callout tipo="dica" titulo="Ativo / Inativo">
            Desative parceiros inativos em vez de excluir — preserva o histórico de operações.
        </Callout>
    </Box>
);

const SecaoProduto: React.FC = () => (
    <Box>
        <TabelaSimples
            cabecalho={['Tipo', 'Uso', 'Campo exclusivo']}
            linhas={[
                ['PRODUTO', 'Mercadorias, insumos, itens físicos', 'Código de barras, NCM'],
                ['SERVIÇO', 'Prestação de serviços', 'Código de Serviço, Tempo de Execução'],
            ]}
        />
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>📍 Onde acessar</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
            Menu lateral → <strong>Cadastros</strong> → <strong>Produto</strong>
        </Typography>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mt: 2 }}>📋 Como cadastrar</Typography>
        <Passo num={1} titulo="Descrição">
            Nome completo do produto ou serviço (obrigatório). Use descrições claras para facilitar buscas no PDV.
        </Passo>
        <Passo num={2} titulo="Código de Barras">
            EAN/GTIN — usado pelo PDV para busca via scanner. Opcional.
        </Passo>
        <Passo num={3} titulo="NCM (produtos físicos)">
            Nomenclatura Comum do Mercosul — obrigatório para emissão de NF-e.
            O campo oferece autocomplete com os 150+ códigos mais comuns.
        </Passo>
        <Passo num={4} titulo="Dados do Serviço (se tipo = SERVIÇO)" ultimo>
            <strong>Código de Serviço</strong> (LC 116 para NFS-e) e <strong>Tempo de Execução</strong>
            em minutos — usado pela Agenda para calcular slots de atendimento automaticamente.
        </Passo>
        <Callout tipo="info" titulo="Produto × Agenda">
            Serviços com Tempo de Execução preenchido são usados pela Agenda para calcular
            a duração dos agendamentos e a disponibilidade dos profissionais.
        </Callout>
    </Box>
);

const SecaoPreco: React.FC = () => (
    <Box>
        <Typography variant="body2" color="text.secondary" paragraph>
            O sistema trabalha com <strong>Tabelas de Preço</strong> independentes. Cada tabela pode ter
            preços diferentes para o mesmo produto — ideal para atacado, varejo, clientes especiais e delivery.
        </Typography>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>📍 Onde acessar</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
            Menu → <strong>Cadastros</strong> → <strong>Tabela de Preços</strong> (para criar a tabela)
            <br />Menu → <strong>Cadastros</strong> → <strong>Preço do Produto</strong> (para cadastrar preços)
        </Typography>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mt: 2 }}>📋 Como configurar</Typography>
        <Passo num={1} titulo="Criar a Tabela de Preço">
            Acesse Tabela de Preços e crie uma nova com descrição clara:
            "Varejo 2025", "Delivery", "Atacado", etc.
        </Passo>
        <Passo num={2} titulo="Cadastrar o Preço de cada Produto">
            Em Preço do Produto, selecione a <strong>Tabela</strong> e o <strong>Produto</strong>,
            informe o valor de venda e salve.
        </Passo>
        <Passo num={3} titulo="Vincular a Tabela ao ponto de uso" ultimo>
            Informe a tabela no PDV, na operação de venda ou na unidade de Delivery.
            O sistema usará os preços dessa tabela automaticamente.
        </Passo>
        <Callout tipo="dica" titulo="Múltiplas tabelas">
            Um mesmo produto pode ter preços diferentes em cada tabela sem conflito.
            A tabela de preço pode ser vinculada diretamente à Configuração do Delivery.
        </Callout>
    </Box>
);

const SecaoMenu: React.FC = () => (
    <Box>
        <Typography variant="body2" color="text.secondary" paragraph>
            O Gestão funciona como um <strong>ambiente desktop dentro do navegador</strong>.
            Cada tela abre como uma "janela" independente na barra de tarefas inferior.
            Você pode ter múltiplas telas abertas simultâneamente — as inativas permanecem
            carregadas em memória sem perder dados.
        </Typography>
        {[
            { grupo: 'Cadastros', itens: ['🏢 Empresa','👥 Parceiro','📦 Produto','💰 Tabela de Preços','💲 Preço do Produto','🏙️ Cidade','🏷️ Categoria de Produto','🔧 Unidade de Medida'] },
            { grupo: 'Compra e Venda', itens: ['🛒 Venda Easy','🛍️ Compra Easy','🧾 Nota Fiscal','📄 Operação de Nota'] },
            { grupo: 'PDV', itens: ['🖥️ PDV Easy','⚙️ Configuração PDV Easy','📊 Monitor PDV'] },
            { grupo: 'Financeiro', itens: ['💳 Financeiro Easy','📑 Título Financeiro','🏷️ Tipo de Título','💱 Tipo de Negociação'] },
            { grupo: 'Agenda', itens: ['📅 Agenda','⚙️ Configuração da Agenda','👨‍⚕️ Profissionais','🛎️ Serviços'] },
            { grupo: 'Delivery', itens: ['🛵 Pedidos','📋 Monitor de Pedidos','🍽️ Cardápio','⚙️ Configuração Delivery'] },
            { grupo: 'Inteligência', itens: ['📈 Performance de Vendas','💹 Lucratividade','📊 Resumo PDV'] },
        ].map(({ grupo, itens }) => (
            <Box key={grupo} sx={{ mb: 2 }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary"
                    sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                    {grupo}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {itens.map(it => (
                        <Paper key={it} variant="outlined" sx={{ px: 1.5, py: 0.75, borderRadius: 2, fontSize: 13 }}>
                            {it}
                        </Paper>
                    ))}
                </Box>
            </Box>
        ))}
        <Callout tipo="info" titulo="Visibilidade por Licença">
            Os menus variam conforme as licenças contratadas:
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                {['GESTAO','DELIVERY','AGENDA','NFE','NFCE','CTE','ESCOLA'].map(l => (
                    <TagStatus key={l} label={l} cor="#455a64" />
                ))}
            </Box>
        </Callout>
    </Box>
);

const SecaoInteligencia: React.FC = () => (
    <Box>
        <Typography variant="body2" color="text.secondary" paragraph>
            O módulo de Inteligência consolida dados das operações em dashboards analíticos
            em tempo real. Quanto mais completo o cadastro e as operações, mais precisos
            são os indicadores.
        </Typography>
        <TabelaSimples
            cabecalho={['Dashboard', 'O que mostra']}
            linhas={[
                ['Performance de Vendas', 'Ranking de produtos, vendedores e clientes por período'],
                ['Lucratividade', 'Cruza custo de aquisição com preço de venda — margem bruta por produto'],
                ['Resumo PDV', 'Total de vendas, ticket médio e formas de pagamento por turno'],
                ['Saúde Financeira', 'Saldo, inadimplência, contas a pagar e receber'],
                ['Estoque', 'Saldo atual, movimentações e rastreio de estoque'],
                ['Cliente 360', 'Histórico completo de compras e interações por cliente'],
            ]}
        />
        <Callout tipo="info" titulo="Como usar">
            Use os filtros de <strong>período</strong> e <strong>empresa</strong> para recortar
            os dados. Os relatórios são gerados em tempo real a partir das operações registradas.
        </Callout>
    </Box>
);

const SecaoAgenda: React.FC = () => (
    <Box>
        <Typography variant="body2" color="text.secondary" paragraph>
            A Agenda do Gestão é um sistema completo de agendamento online. A configuração
            define uma <strong>Unidade de Atendimento</strong> com serviços, profissionais,
            horários e aparência próprios. Cada unidade gera um link público para
            auto-agendamento dos clientes.
        </Typography>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>📍 Onde acessar</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
            Menu → <strong>Agenda</strong> → <strong>Configuração da Agenda</strong>
        </Typography>
        {[
            { step: 'Step 1 — Unidade de Atendimento', campos: [
                ['Nome da Unidade','Nome exibido para os clientes no app público'],
                ['Empresa','Empresa emitente vinculada a esta unidade'],
                ['Prazo de Agenda','Quantos dias no futuro o cliente pode agendar'],
                ['Confirmação Automática','Confirma o agendamento sem validação manual'],
                ['Exige Confirmação de Número','Valida WhatsApp do cliente antes de confirmar'],
            ]},
            { step: 'Step 2 — Mensagens Automáticas (WhatsApp)', campos: [
                ['Mensagem de Solicitação','Enviada quando o cliente solicita um horário'],
                ['Mensagem de Confirmação','Enviada quando o agendamento é confirmado'],
                ['Mensagem de Conclusão','Enviada após o atendimento ser finalizado'],
                ['Mensagem de Cancelamento','Enviada quando o agendamento é cancelado'],
            ]},
            { step: 'Step 3 — Serviços', campos: [
                ['Produto/Serviço','Selecione o serviço já cadastrado no sistema'],
                ['Tempo de Execução','Herdado do cadastro do produto (em minutos)'],
                ['Valor','Valor de venda específico para a unidade'],
            ]},
            { step: 'Step 4 — Profissionais', campos: [
                ['Parceiro','Parceiro correspondente ao profissional'],
                ['Expediente por dia','Horário de início e fim para cada dia da semana'],
            ]},
        ].map(({ step, campos }) => (
            <Box key={step} sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight={700} color="primary" gutterBottom>
                    {step}
                </Typography>
                <TabelaSimples cabecalho={['Campo','Descrição']} linhas={campos} />
            </Box>
        ))}
        <Typography variant="subtitle2" fontWeight={700} color="primary" gutterBottom>
            Step 5 — Temas Visuais
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, my: 1 }}>
            {[
                { nome: 'Azul Moderno', bg: 'linear-gradient(135deg,#1565c0,#42a5f5)' },
                { nome: 'Dourado Elegante', bg: 'linear-gradient(135deg,#f9a825,#fff176)' },
                { nome: 'Verde Natureza', bg: 'linear-gradient(135deg,#2e7d32,#66bb6a)' },
                { nome: 'Roxo Vibrante', bg: 'linear-gradient(135deg,#6a1b9a,#ce93d8)' },
                { nome: 'Rosa Suave', bg: 'linear-gradient(135deg,#e91e63,#f48fb1)' },
                { nome: 'Dark Minimalista', bg: 'linear-gradient(135deg,#212121,#616161)' },
                { nome: 'Barber Classic', bg: 'linear-gradient(135deg,#5d4037,#a1887f)' },
                { nome: 'Barber Premium', bg: 'linear-gradient(135deg,#bf360c,#ff7043)' },
                { nome: 'Barber Urban', bg: 'linear-gradient(135deg,#263238,#546e7a)' },
            ].map(t => (
                <Box key={t.nome} sx={{ width: 80, borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ height: 32, background: t.bg }} />
                    <Box sx={{ p: 0.75, fontSize: 10, fontWeight: 600, textAlign: 'center', bgcolor: 'background.paper' }}>
                        {t.nome}
                    </Box>
                </Box>
            ))}
        </Box>
        <Callout tipo="dica" titulo="Link Público">
            Após configurar e salvar, a unidade gera um link público que pode ser compartilhado
            com clientes via WhatsApp ou redes sociais. O cliente agenda sem precisar de login.
        </Callout>
    </Box>
);

const SecaoDelivery: React.FC = () => (
    <Box>
        <Typography variant="body2" color="text.secondary" paragraph>
            O módulo Delivery oferece um cardápio online acessível por qualquer dispositivo,
            com pedidos que chegam direto ao sistema. A configuração define horários, modalidades
            de entrega, taxas, formas de pagamento e identidade visual.
        </Typography>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>📍 Onde acessar</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
            Menu → <strong>Delivery</strong> → <strong>Configuração Delivery</strong>
        </Typography>
        {[
            { step: 'Step 1 — Dados da Unidade', campos: [
                ['Nome','Nome do estabelecimento exibido no cardápio (obrigatório)'],
                ['Chave Pública','Identificador único para o link do cardápio (obrigatório)'],
                ['Empresa','Empresa emitente vinculada aos pedidos (obrigatório)'],
                ['Tabela de Preço','Tabela de preços usada no cardápio (opcional)'],
                ['Aceita Pedidos','Liga/desliga recebimento de novos pedidos'],
            ]},
            { step: 'Step 2 — Modalidades de Entrega', campos: [
                ['Permite Entrega','Habilita entrega no endereço do cliente'],
                ['Permite Retirada','Habilita retirada no balcão'],
                ['Taxa de Entrega','Valor cobrado pela entrega (R$)'],
                ['Pedido Mínimo','Valor mínimo do pedido'],
                ['Tempo de Entrega','Estimativa em minutos (padrão: 40 min)'],
                ['Mensagem de Boas-Vindas','Texto exibido na página inicial do cardápio'],
            ]},
            { step: 'Step 3 — Horários de Funcionamento', campos: [
                ['Por dia da semana','Ative/desative e defina horário de abertura e fechamento para cada dia'],
            ]},
        ].map(({ step, campos }) => (
            <Box key={step} sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight={700} color="primary" gutterBottom>{step}</Typography>
                <TabelaSimples cabecalho={['Campo','Descrição']} linhas={campos} />
            </Box>
        ))}
        <Typography variant="subtitle2" fontWeight={700} color="primary" gutterBottom>
            Status dos Pedidos — Monitor Kanban
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, my: 1 }}>
            {[
                { label: 'RECEBIDO', cor: '#f57f17' }, { label: 'CONFIRMADO', cor: '#1565c0' },
                { label: 'PREPARANDO', cor: '#6a1b9a' }, { label: 'SAIU', cor: '#2e7d32' },
                { label: 'ENTREGUE', cor: '#2e7d32' }, { label: 'CANCELADO', cor: '#b71c1c' },
            ].map(s => <TagStatus key={s.label} label={s.label} cor={s.cor} />)}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            No Monitor, arraste os pedidos entre colunas para atualizar o status.
            O cliente pode ser notificado via WhatsApp em cada mudança.
        </Typography>
        <Callout tipo="dica" titulo="Cardápio Online">
            O cardápio é acessível pelo link gerado após salvar a configuração.
            Os pedidos chegam em tempo real na tela Monitor de Pedidos.
        </Callout>
    </Box>
);

const SecaoPdvConfig: React.FC = () => (
    <Box>
        <Typography variant="body2" color="text.secondary" paragraph>
            Cada ponto de venda precisa de uma configuração própria. Ela define comportamento,
            impressora, formas de pagamento e aparência visual para aquele caixa específico.
        </Typography>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>📍 Onde acessar</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
            Menu → <strong>PDV</strong> → <strong>Configuração de PDV (Easy)</strong>
        </Typography>
        {[
            { step: 'Step 1 — Identificação', campos: [
                ['Descrição','Nome do caixa (ex: Caixa 1, PDV Loja Central) — obrigatório'],
                ['Empresa','Empresa emitente dos documentos — obrigatório'],
                ['Parceiro','Parceiro padrão (consumidor final) — obrigatório'],
                ['Operação de Nota','Operação fiscal das vendas (NFC-e, cupom) — obrigatório'],
                ['Série','Série do documento fiscal'],
            ]},
            { step: 'Step 2 — Impressão', campos: [
                ['Tipo de Impressão','TXT, ESCPOS ou PDF'],
                ['Impressora','Nome da impressora instalada no computador do caixa'],
                ['Largura do Cupom','58mm ou 80mm'],
                ['Mensagem de Rodapé','Texto personalizado impresso no final do cupom'],
            ]},
            { step: 'Step 3 — Pagamentos', campos: [
                ['Tipos de Título','Formas de pagamento disponíveis neste caixa (Dinheiro, Pix, Cartão…)'],
                ['Tipos de Negociação','À vista, Parcelado, Fiado, etc.'],
                ['Financeiro Inserido como Baixado','Títulos já entram pagos no financeiro automaticamente'],
            ]},
            { step: 'Step 4 — Comportamento', campos: [
                ['Mostrar Item Removido','Exibe visualmente quando um item é removido'],
                ['Permite Alterar Vendedor','Operador pode trocar o vendedor durante a venda'],
                ['Permite Múltiplas Vendas','Mais de uma venda aberta simultaneamente'],
            ]},
        ].map(({ step, campos }) => (
            <Box key={step} sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight={700} color="primary" gutterBottom>{step}</Typography>
                <TabelaSimples cabecalho={['Campo','Descrição']} linhas={campos} />
            </Box>
        ))}
        <Typography variant="subtitle2" fontWeight={700} color="primary" gutterBottom>
            Step 5 — Temas Visuais do PDV
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, my: 1 }}>
            {[
                { nome: 'Mercadeiro', bg: 'linear-gradient(135deg,#388e3c,#81c784)' },
                { nome: 'Vitrine', bg: 'linear-gradient(135deg,#1565c0,#64b5f6)' },
                { nome: 'Varejo Forte', bg: 'linear-gradient(135deg,#b71c1c,#ef9a9a)' },
                { nome: 'Sabor e Cor', bg: 'linear-gradient(135deg,#f57c00,#ffcc80)' },
                { nome: 'Bistrô', bg: 'linear-gradient(135deg,#37474f,#90a4ae)' },
                { nome: 'Fluxo', bg: 'linear-gradient(135deg,#4a148c,#ba68c8)' },
                { nome: 'Nuance', bg: 'linear-gradient(135deg,#263238,#78909c)' },
                { nome: 'Pró-Caixa', bg: 'linear-gradient(135deg,#0d47a1,#1976d2)' },
                { nome: 'Pétala', bg: 'linear-gradient(135deg,#880e4f,#f06292)' },
                { nome: 'Horizonte', bg: 'linear-gradient(135deg,#e65100,#ffb74d)' },
                { nome: 'Raízes', bg: 'linear-gradient(135deg,#33691e,#aed581)' },
            ].map(t => (
                <Box key={t.nome} sx={{ width: 80, borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ height: 32, background: t.bg }} />
                    <Box sx={{ p: 0.75, fontSize: 10, fontWeight: 600, textAlign: 'center', bgcolor: 'background.paper' }}>
                        {t.nome}
                    </Box>
                </Box>
            ))}
        </Box>
    </Box>
);

const SecaoPdvVenda: React.FC = () => (
    <Box>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>📍 Onde acessar</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
            Menu → <strong>PDV</strong> → <strong>PDV Easy</strong>
        </Typography>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mt: 2 }}>🔓 Abertura de Caixa</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
            Ao acessar o PDV, o sistema verifica se o caixa está aberto. Se não estiver,
            exibe o Dialog de Abertura: selecione a <strong>Configuração de PDV</strong>
            e informe o valor em dinheiro no caixa (troco inicial).
        </Typography>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mt: 2 }}>🛒 Realizando uma Venda</Typography>
        <Passo num={1} titulo="Buscar Produto">
            No campo de busca, digite o <strong>nome</strong> ou <strong>código de barras</strong>.
            Use um scanner para agilizar. O sistema busca em tempo real.
        </Passo>
        <Passo num={2} titulo="Adicionar ao Carrinho">
            Selecione o produto na lista. Altere a quantidade com o <strong>NumPad</strong> integrado.
        </Passo>
        <Passo num={3} titulo="Desconto por Item (opcional)">
            Toque no item para abrir o Dialog de Desconto. Informe percentual ou valor e confirme.
        </Passo>
        <Passo num={4} titulo="Identificar Cliente (opcional)">
            Clique no ícone de <strong>Parceiro</strong> para buscar e vincular o cliente à venda.
        </Passo>
        <Passo num={5} titulo="Finalizar Venda">
            Clique em <strong>Finalizar</strong>. Selecione a forma de pagamento, informe o
            valor recebido e confirme.
        </Passo>
        <Passo num={6} titulo="Impressão e Troco" ultimo>
            O sistema calcula o troco e envia o cupom à impressora configurada.
        </Passo>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mt: 2 }}>📊 Outras Funções</Typography>
        <TabelaSimples
            cabecalho={['Função','Descrição']}
            linhas={[
                ['Movimentação de Caixa','Registra entradas (suprimento) e saídas (sangria)'],
                ['Consulta de Preço','Consulta o preço sem adicionar à venda'],
                ['Cancelar Item','Remove um item da venda atual'],
                ['Cancelar Venda','Cancela a venda em andamento completamente'],
                ['Múltiplas Vendas','Abre nova venda em paralelo (se habilitado)'],
            ]}
        />
        <Callout tipo="aviso" titulo="Emissão Fiscal">
            Conforme a operação configurada, o sistema pode gerar automaticamente
            <strong> NFC-e</strong> a cada venda. Em caso de queda de internet,
            opera em <strong>modo offline</strong> e transmite ao reconectar.
        </Callout>
    </Box>
);

const SecaoNota: React.FC = () => (
    <Box>
        <Typography variant="body2" color="text.secondary" paragraph>
            As telas de <strong>Venda Easy</strong>, <strong>Compra Easy</strong> e <strong>Nota Easy</strong>
            são formas simplificadas de registrar vendas e compras. O comportamento de cada nota é definido
            pela <strong>Operação de Nota</strong> vinculada (se gera NF-e, movimenta estoque, gera financeiro, etc.).
        </Typography>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>📍 Onde acessar</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
            Menu → <strong>Compra e Venda</strong> → <strong>Venda Easy</strong> ou <strong>Compra Easy</strong>
        </Typography>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mt: 2 }}>🛒 Fluxo de uma Venda</Typography>
        <Passo num={1} titulo="Nova Nota">
            Clique em <strong>Nova Venda</strong>. O sistema abre o editor com as abas
            Cabeçalho, Itens, Financeiro e Rodapé.
        </Passo>
        <Passo num={2} titulo="Cabeçalho">
            Selecione a <strong>Empresa</strong> emitente, a <strong>Operação de Nota</strong>
            (define comportamento fiscal) e o <strong>Parceiro</strong> (cliente).
            Datas de emissão e saída são preenchidas automaticamente.
        </Passo>
        <Passo num={3} titulo="Itens">
            Busque produtos pelo nome ou código. Informe quantidade e, se necessário, altere
            o valor unitário. O subtotal é calculado automaticamente.
        </Passo>
        <Passo num={4} titulo="Financeiro">
            Defina o <strong>Tipo de Negociação</strong> (à vista, parcelado), o <strong>Tipo de Título</strong>
            e as datas de vencimento. As parcelas são geradas automaticamente.
        </Passo>
        <Passo num={5} titulo="Salvar e Emitir" ultimo>
            Salve a nota. Conforme a operação configurada, o sistema pode emitir
            automaticamente <strong>NF-e</strong> ou <strong>NFC-e</strong> ao salvar.
        </Passo>
        <TabelaSimples
            cabecalho={['Campo', 'Fórmula / Regra']}
            linhas={[
                ['Subtotal do Item', 'VALOR UNITÁRIO × QUANTIDADE'],
                ['Total do Item', 'SUBTOTAL − DESCONTO DO ITEM'],
                ['Total da Nota', 'Soma dos totais dos itens'],
            ]}
        />
        <Callout tipo="info" titulo="Operação de Nota">
            A Operação de Nota controla se a venda movimenta estoque, gera financeiro,
            emite nota fiscal e qual CFOP é utilizado. Configure em
            <strong> Cadastros → Operação de Nota</strong>.
        </Callout>
        <Callout tipo="dica" titulo="Devolução">
            Para devolver uma nota, acesse-a na lista e use a opção <strong>Devolver</strong>.
            O sistema cria uma nota de devolução com os mesmos itens.
        </Callout>
    </Box>
);

const SecaoFinanceiro: React.FC = () => (
    <Box>
        <Typography variant="body2" color="text.secondary" paragraph>
            O módulo financeiro gerencia títulos a <strong>receber</strong> (receitas) e a
            <strong> pagar</strong> (despesas). Os títulos são gerados automaticamente pelas
            vendas e compras, ou podem ser lançados manualmente.
        </Typography>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>📍 Onde acessar</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
            Menu → <strong>Financeiro</strong> → <strong>Financeiro Easy</strong>
        </Typography>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mt: 2 }}>📊 Situações do Título</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, my: 1 }}>
            {[
                { label: 'INCLUÍDO', cor: '#1565c0' },
                { label: 'BAIXADO', cor: '#2e7d32' },
                { label: 'PARCIALMENTE BAIXADO', cor: '#f57f17' },
                { label: 'ESTORNADO', cor: '#6a1b9a' },
                { label: 'CANCELADO', cor: '#b71c1c' },
                { label: 'PROVISÃO', cor: '#546e7a' },
            ].map(s => <TagStatus key={s.label} label={s.label} cor={s.cor} />)}
        </Box>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mt: 2 }}>📋 Ações disponíveis</Typography>
        <TabelaSimples
            cabecalho={['Ação', 'O que faz']}
            linhas={[
                ['Baixar', 'Quita o título (total ou parcial) — registra data e valor recebido/pago'],
                ['Estornar', 'Desfaz uma baixa, retornando o título a INCLUÍDO'],
                ['Cancelar', 'Cancela definitivamente o título (sem estorno)'],
                ['Recibo', 'Gera PDF de recibo para o cliente'],
            ]}
        />
        <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mt: 2 }}>🔄 Financeiro Recorrente</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
            Para lançamentos periódicos (mensalidades, aluguéis, assinaturas), use
            <strong> Financeiro Recorrente</strong>. Configure uma vez e o sistema gera
            os títulos automaticamente nas datas definidas.
        </Typography>
        <TabelaSimples
            cabecalho={['Campo', 'Descrição']}
            linhas={[
                ['Parceiro', 'Cliente ou fornecedor do lançamento'],
                ['Valor', 'Valor de cada parcela gerada'],
                ['Número de Parcelas', 'Quantas repetições serão criadas'],
                ['Primeira Parcela', 'Data do primeiro título'],
                ['Intervalo', 'Espaçamento: ex: 1 mês, 7 dias, 1 ano'],
                ['Tipo de Movimento', 'ENTRADA (receber) ou SAÍDA (pagar)'],
            ]}
        />
        <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mt: 2 }}>📣 Régua de Cobrança</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
            A Régua de Cobrança envia mensagens automáticas via <strong>WhatsApp</strong>
            para clientes com títulos em aberto, conforme regras configuráveis por dias de vencimento.
        </Typography>
        <Passo num={1} titulo="Criar uma Regra">
            Acesse <strong>Financeiro → Régua de Cobrança</strong>. Informe nome, tipo de evento
            (antes/no/após vencimento) e quantos dias de offset.
        </Passo>
        <Passo num={2} titulo="Definir a Mensagem">
            Escreva o template com placeholders:
            <Box component="span" sx={{ fontFamily: 'monospace', bgcolor: '#f5f7fa', px: 0.5, borderRadius: 0.5 }}>
                {'{PARCEIRO}'}
            </Box>{' '}
            <Box component="span" sx={{ fontFamily: 'monospace', bgcolor: '#f5f7fa', px: 0.5, borderRadius: 0.5 }}>
                {'{VALOR}'}
            </Box>{' '}
            <Box component="span" sx={{ fontFamily: 'monospace', bgcolor: '#f5f7fa', px: 0.5, borderRadius: 0.5 }}>
                {'{VENCIMENTO}'}
            </Box>{' '}
            <Box component="span" sx={{ fontFamily: 'monospace', bgcolor: '#f5f7fa', px: 0.5, borderRadius: 0.5 }}>
                {'{DIAS}'}
            </Box>
        </Passo>
        <Passo num={3} titulo="Executar" ultimo>
            A régua pode ser executada manualmente ou automaticamente pelo sistema.
            Cada execução fica registrada no <strong>Histórico</strong>.
        </Passo>
        <Callout tipo="aviso" titulo="Pré-requisito">
            O celular do parceiro deve estar cadastrado e o serviço de WhatsApp
            deve estar conectado para o envio funcionar.
        </Callout>
    </Box>
);

const SecaoEstoque: React.FC = () => (
    <Box>
        <Typography variant="body2" color="text.secondary" paragraph>
            O estoque é movimentado automaticamente pelas operações de venda e compra
            (conforme a Operação de Nota). Use as telas de estoque para consultar saldos,
            rastrear movimentações e realizar inventários.
        </Typography>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>📍 Onde acessar</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
            Menu → <strong>Estoque</strong>
        </Typography>
        <TabelaSimples
            cabecalho={['Tela', 'O que faz']}
            linhas={[
                ['Estoque', 'Consulta o saldo atual de cada produto por empresa'],
                ['Inventário', 'Ajusta o saldo do estoque por contagem física'],
                ['Rastreio de Estoque', 'Histórico completo de entradas e saídas de um produto'],
                ['Categoria de Produto', 'Agrupa produtos para filtragem e relatórios'],
                ['Marca', 'Categoriza produtos por fabricante ou marca'],
            ]}
        />
        <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mt: 2 }}>📦 Como fazer Inventário</Typography>
        <Passo num={1} titulo="Abrir o Inventário">
            Acesse <strong>Estoque → Inventário</strong> e selecione a empresa.
        </Passo>
        <Passo num={2} titulo="Informar as Quantidades">
            Para cada produto, informe a quantidade contada fisicamente.
            Produtos não informados mantêm o saldo atual.
        </Passo>
        <Passo num={3} titulo="Confirmar" ultimo>
            Ao confirmar, o sistema ajusta o saldo (entradas ou saídas de ajuste)
            para refletir a contagem real.
        </Passo>
        <Callout tipo="info" titulo="Inteligência de Estoque">
            Use o dashboard <strong>Inteligência → Estoque</strong> para ver
            Curva ABC, produtos zerados, negativos e giro de estoque por período.
        </Callout>
        <Callout tipo="aviso" titulo="Saldo negativo">
            Saldo negativo indica lançamentos fora de ordem (venda antes da compra).
            Corrija registrando a entrada do produto com a data correta.
        </Callout>
    </Box>
);

const SecaoFiscal: React.FC = () => (
    <Box>
        <Typography variant="body2" color="text.secondary" paragraph>
            O módulo fiscal gerencia a emissão e transmissão de documentos eletrônicos
            à SEFAZ: <strong>NF-e</strong> (nota fiscal eletrônica), <strong>NFC-e</strong>
            (nota fiscal de consumidor) e <strong>CT-e</strong> (conhecimento de transporte).
        </Typography>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>⚙️ Configuração Fiscal</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
            Menu → <strong>Configurações Fiscais → Configuração Fiscal</strong>
        </Typography>
        <TabelaSimples
            cabecalho={['Campo', 'Descrição']}
            linhas={[
                ['Empresa', 'Empresa para a qual a configuração se aplica'],
                ['Ambiente', 'PRODUÇÃO (notas reais) ou HOMOLOGAÇÃO (testes)'],
                ['Regime Tributário', 'Simples Nacional, Lucro Presumido, Lucro Real'],
                ['Série NF-e / NFC-e', 'Série dos documentos fiscais emitidos'],
                ['CSC / Token NFC-e', 'Código de Segurança do Consumidor (obrigatório para NFC-e)'],
            ]}
        />
        <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mt: 2 }}>🔐 Certificado Digital</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
            O certificado digital A1 (arquivo .pfx) é necessário para assinar e transmitir
            documentos fiscais. Faça o upload em
            <strong> Configurações Fiscais → Certificado Digital</strong>.
        </Typography>
        <Callout tipo="aviso" titulo="Validade do Certificado">
            Certificados A1 têm validade de 1 ou 3 anos. O sistema alerta quando
            o vencimento está próximo. Renove antes do vencimento para não interromper a emissão.
        </Callout>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mt: 2 }}>🧾 Fluxo de Emissão NF-e</Typography>
        <Passo num={1} titulo="Criar a Nota">
            Registre a venda/compra em <strong>Venda Easy</strong> ou <strong>Nota Easy</strong>
            com uma Operação de Nota que gera NF-e.
        </Passo>
        <Passo num={2} titulo="Transmitir">
            Clique em <strong>Emitir NF-e</strong>. O sistema assina com o certificado digital
            e transmite à SEFAZ.
        </Passo>
        <Passo num={3} titulo="Autorizada" ultimo>
            A SEFAZ retorna com a chave de acesso e protocolo. O XML e DANFE ficam
            disponíveis para download e impressão.
        </Passo>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mt: 2 }}>🚚 CT-e (Conhecimento de Transporte)</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
            Para transportadoras, o CT-e documenta o serviço de transporte de cargas.
            Acesse em <strong>CT-e</strong> no menu lateral. Requer configuração de
            veículos, alíquotas de ICMS e operações de CT-e.
        </Typography>
        <TabelaSimples
            cabecalho={['Tela', 'Função']}
            linhas={[
                ['Painel Fiscal', 'Visão geral das notas emitidas, situações e pendências'],
                ['Exportação XML', 'Download de XMLs em lote por período para arquivamento'],
                ['IBPT', 'Tabela de carga tributária aproximada (impressão nas notas)'],
                ['Sintegra', 'Geração do arquivo SINTEGRA para obrigação acessória estadual'],
            ]}
        />
        <Callout tipo="dica" titulo="Rejeições da SEFAZ">
            Em caso de rejeição, o código de erro e a descrição são exibidos na tela.
            Corrija os dados apontados e retransmita. As rejeições mais comuns são:
            NCM inválido, IE do destinatário incorreta e CFOP incompatível com a operação.
        </Callout>
    </Box>
);

const SecaoAcademia: React.FC = () => (
    <Box>
        <Typography variant="body2" color="text.secondary" paragraph>
            O módulo Academia (licença <TagStatus label="ESCOLA" cor="#880e4f" />) gerencia
            turmas, alunos, professores, frequência e comissões para escolas, academias
            e centros de treinamento.
        </Typography>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>📍 Onde acessar</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
            Menu → <strong>Academia</strong>
        </Typography>
        <TabelaSimples
            cabecalho={['Tela', 'Função']}
            linhas={[
                ['Turma', 'Grupos de alunos com professor e horário definidos'],
                ['Aluno', 'Cadastro de alunos (vinculados a um Parceiro)'],
                ['Professor', 'Cadastro de professores (vinculados a um Parceiro)'],
                ['Aula / Turma', 'Registro de aulas realizadas por turma'],
                ['Frequência de Aluno', 'Controle de presença por aula'],
                ['Comissão de Professores', 'Cálculo automático de comissão por aulas ministradas'],
            ]}
        />
        <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mt: 2 }}>📋 Fluxo de Cadastro</Typography>
        <Passo num={1} titulo="Cadastrar Professor">
            Acesse <strong>Academia → Professor</strong>. Vincule a um Parceiro já cadastrado
            (o parceiro contém CPF, contato e dados financeiros).
        </Passo>
        <Passo num={2} titulo="Cadastrar Turma">
            Em <strong>Academia → Turma</strong>, informe o nome, professor responsável,
            horários e capacidade máxima.
        </Passo>
        <Passo num={3} titulo="Matricular Alunos">
            Em <strong>Academia → Aluno</strong>, cadastre o aluno e vincule às turmas.
        </Passo>
        <Passo num={4} titulo="Registrar Aulas e Frequência">
            Em <strong>Academia → Aula / Turma</strong>, registre cada aula realizada.
            Em <strong>Frequência</strong>, marque presença/ausência por aluno.
        </Passo>
        <Passo num={5} titulo="Calcular Comissão" ultimo>
            Acesse <strong>Academia → Comissão de Professores</strong>. Selecione o período,
            e o sistema calcula automaticamente o valor a pagar para cada professor
            com base nas aulas ministradas e no financeiro dos alunos.
        </Passo>
        <Callout tipo="info" titulo="Comissão">
            O cálculo percorre: Professor → Turmas → Alunos matriculados → Títulos
            financeiros dos alunos. Você pode excluir títulos individuais antes de gerar
            o relatório final em PDF.
        </Callout>
    </Box>
);

// ── Mapa id → componente de conteúdo ──────────────────────────────────────
const CONTEUDO: Record<string, React.FC> = {
    empresa:      SecaoEmpresa,
    parceiro:     SecaoParceiro,
    produto:      SecaoProduto,
    preco:        SecaoPreco,
    menu:         SecaoMenu,
    inteligencia: SecaoInteligencia,
    agenda:       SecaoAgenda,
    delivery:     SecaoDelivery,
    'pdv-config': SecaoPdvConfig,
    'pdv-venda':  SecaoPdvVenda,
    nota:         SecaoNota,
    financeiro:   SecaoFinanceiro,
    estoque:      SecaoEstoque,
    fiscal:       SecaoFiscal,
    academia:     SecaoAcademia,
};

// ── Página principal ───────────────────────────────────────────────────────
const SIDEBAR_W = 260;

export default function Documentacao() {
    const [searchParams] = useSearchParams();
    const [busca, setBusca] = useState('');
    const [secaoAtiva, setSecaoAtiva] = useState<string>('empresa');
    const [showTop, setShowTop] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [carregando, setCarregando] = useState(true);
    const isMobile = useMediaQuery('(max-width:768px)');
    const mainRef = useRef<HTMLDivElement>(null);

    // Navegar para seção via ?secao=xxx
    useEffect(() => {
        const s = searchParams.get('secao');
        if (s && SECOES.find(x => x.id === s)) {
            setTimeout(() => {
                document.getElementById(s)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                setSecaoAtiva(s);
            }, 600);
        }
        setCarregando(false);
    }, [searchParams]);

    // Detecta seção visível ao fazer scroll
    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => {
                entries.forEach(e => {
                    if (e.isIntersecting) setSecaoAtiva(e.target.id);
                });
            },
            { rootMargin: '-80px 0px -60% 0px', threshold: 0 }
        );
        SECOES.forEach(s => {
            const el = document.getElementById(s.id);
            if (el) observer.observe(el);
        });
        return () => observer.disconnect();
    }, []);

    // Botão "voltar ao topo"
    useEffect(() => {
        const onScroll = () => setShowTop(window.scrollY > 400);
        window.addEventListener('scroll', onScroll);
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    const irPara = useCallback((id: string) => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setSecaoAtiva(id);
        setDrawerOpen(false);
    }, []);

    const secoesFiltradas = SECOES.filter(s =>
        busca === '' || s.label.toLowerCase().includes(busca.toLowerCase())
    );

    const sidebar = (
        <Box
            sx={{
                width: SIDEBAR_W, flexShrink: 0,
                display: 'flex', flexDirection: 'column', gap: 0,
            }}
        >
            {/* Busca */}
            <Box sx={{ p: 2 }}>
                <TextField
                    size="small" fullWidth placeholder="Buscar seção…"
                    value={busca} onChange={e => setBusca(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" color="action" />
                            </InputAdornment>
                        ),
                        sx: { borderRadius: 2, fontSize: 13 },
                    }}
                />
            </Box>
            <Divider />
            {/* Navegação */}
            <List dense sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
                {secoesFiltradas.map(s => (
                    <ListItemButton
                        key={s.id}
                        selected={secaoAtiva === s.id}
                        onClick={() => irPara(s.id)}
                        sx={{
                            borderRadius: 2, mx: 1, mb: 0.5,
                            '&.Mui-selected': {
                                bgcolor: `${s.cor}14`,
                                '&:hover': { bgcolor: `${s.cor}22` },
                            },
                        }}
                    >
                        <ListItemIcon sx={{ minWidth: 36, color: secaoAtiva === s.id ? s.cor : 'text.secondary' }}>
                            {s.icon}
                        </ListItemIcon>
                        <ListItemText
                            primary={s.label}
                            primaryTypographyProps={{
                                fontSize: 13, fontWeight: secaoAtiva === s.id ? 700 : 400,
                                color: secaoAtiva === s.id ? s.cor : 'text.primary',
                            }}
                        />
                        {secaoAtiva === s.id && (
                            <ArrowRightIcon sx={{ color: s.cor, fontSize: 18 }} />
                        )}
                    </ListItemButton>
                ))}
                {secoesFiltradas.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 2 }}>
                        Nenhuma seção encontrada.
                    </Typography>
                )}
            </List>
        </Box>
    );

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f0f4f8', display: 'flex', flexDirection: 'column' }}>
            {carregando && <LinearProgress />}

            {/* ── Header ── */}
            <Box
                component="header"
                sx={{
                    position: 'sticky', top: 0, zIndex: 100,
                    bgcolor: 'white',
                    borderBottom: '1px solid', borderColor: 'divider',
                    boxShadow: '0 1px 8px rgba(0,0,0,0.08)',
                }}
            >
                <Box sx={{
                    maxWidth: 1200, mx: 'auto', px: 3,
                    height: 64, display: 'flex', alignItems: 'center', gap: 2,
                }}>
                    {isMobile && (
                        <IconButton onClick={() => setDrawerOpen(true)} size="small">
                            <MenuIcon />
                        </IconButton>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                            component="img"
                            src="/logo128.png"
                            alt="Gestão"
                            sx={{ width: 32, height: 32, borderRadius: 1 }}
                            onError={(e: any) => { e.target.style.display = 'none'; }}
                        />
                        <Box>
                            <Typography variant="subtitle2" fontWeight={800} lineHeight={1.2}>
                                Gestão AppCodesh
                            </Typography>
                            <Typography variant="caption" color="text.secondary" lineHeight={1}>
                                Documentação
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ flex: 1 }} />
                    <Chip
                        label="ERP SaaS"
                        size="small"
                        sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 600, fontSize: 11 }}
                    />
                </Box>
            </Box>

            {/* ── Hero ── */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #0d47a1 0%, #1565c0 50%, #1976d2 100%)',
                    color: 'white', py: { xs: 4, md: 6 }, px: 3,
                }}
            >
                <Box sx={{ maxWidth: 700, mx: 'auto', textAlign: 'center' }}>
                    <Typography variant="h4" fontWeight={800} gutterBottom>
                        Central de Ajuda
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.85, mb: 3 }}>
                        Guia completo para cadastros, configurações e operações do sistema.
                    </Typography>
                    <Box sx={{ position: 'relative', maxWidth: 480, mx: 'auto' }}>
                        <TextField
                            fullWidth
                            placeholder="O que você procura? Ex: cadastrar produto, configurar PDV…"
                            value={busca}
                            onChange={e => setBusca(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: 'rgba(0,0,0,0.4)' }} />
                                    </InputAdornment>
                                ),
                                sx: {
                                    bgcolor: 'white', borderRadius: 3,
                                    fontSize: 14,
                                    '& fieldset': { border: 'none' },
                                },
                            }}
                        />
                    </Box>
                    {busca && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                            {secoesFiltradas.map(s => (
                                <Chip
                                    key={s.id} label={s.label}
                                    onClick={() => { irPara(s.id); setBusca(''); }}
                                    sx={{
                                        bgcolor: 'rgba(255,255,255,0.2)', color: 'white',
                                        borderColor: 'rgba(255,255,255,0.4)', border: '1px solid',
                                        cursor: 'pointer', fontWeight: 600,
                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.35)' },
                                    }}
                                />
                            ))}
                            {secoesFiltradas.length === 0 && (
                                <Typography variant="body2" sx={{ opacity: 0.75 }}>
                                    Nenhuma seção encontrada para "{busca}"
                                </Typography>
                            )}
                        </Box>
                    )}
                </Box>
            </Box>

            {/* ── Cards de atalho ── */}
            {!busca && (
                <Box sx={{ bgcolor: 'white', borderBottom: '1px solid', borderColor: 'divider', py: 2, px: 3 }}>
                    <Box sx={{
                        maxWidth: 1200, mx: 'auto',
                        display: 'flex', gap: 1.5, overflowX: 'auto',
                        pb: 0.5,
                        '&::-webkit-scrollbar': { height: 4 },
                        '&::-webkit-scrollbar-thumb': { borderRadius: 2, bgcolor: '#ccc' },
                    }}>
                        {SECOES.map(s => (
                            <Box
                                key={s.id}
                                onClick={() => irPara(s.id)}
                                sx={{
                                    flexShrink: 0, cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', gap: 1,
                                    px: 2, py: 1, borderRadius: 2,
                                    border: '1px solid',
                                    borderColor: secaoAtiva === s.id ? s.cor : 'divider',
                                    bgcolor: secaoAtiva === s.id ? `${s.cor}10` : 'transparent',
                                    color: secaoAtiva === s.id ? s.cor : 'text.secondary',
                                    transition: 'all 0.15s',
                                    '&:hover': { bgcolor: `${s.cor}10`, borderColor: s.cor, color: s.cor },
                                }}
                            >
                                <Box sx={{ fontSize: 18, display: 'flex' }}>{s.icon}</Box>
                                <Typography variant="caption" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
                                    {s.label}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

            {/* ── Layout principal ── */}
            <Box
                sx={{
                    maxWidth: 1200, mx: 'auto', width: '100%',
                    display: 'flex', flex: 1, px: { xs: 0, md: 3 }, py: 3, gap: 3,
                }}
            >
                {/* Sidebar desktop */}
                {!isMobile && (
                    <Paper
                        variant="outlined"
                        sx={{
                            width: SIDEBAR_W, flexShrink: 0, borderRadius: 3,
                            position: 'sticky', top: 72,
                            maxHeight: 'calc(100vh - 88px)',
                            display: 'flex', flexDirection: 'column',
                            overflow: 'hidden',
                        }}
                    >
                        {sidebar}
                    </Paper>
                )}

                {/* Drawer mobile */}
                <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
                    <Box sx={{ width: SIDEBAR_W, pt: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1 }}>
                            <IconButton onClick={() => setDrawerOpen(false)} size="small">
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                        {sidebar}
                    </Box>
                </Drawer>

                {/* Conteúdo */}
                <Box ref={mainRef} sx={{ flex: 1, minWidth: 0 }}>
                    {SECOES.map((s, i) => {
                        const Conteudo = CONTEUDO[s.id];
                        return (
                            <Fade key={s.id} in timeout={300 + i * 50}>
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        borderRadius: 3, p: { xs: 2.5, md: 4 },
                                        mb: 3,
                                        '&:last-child': { mb: 0 },
                                    }}
                                >
                                    <SecaoHeader secao={s} />
                                    <Conteudo />
                                </Paper>
                            </Fade>
                        );
                    })}
                </Box>
            </Box>

            {/* Rodapé */}
            <Box
                component="footer"
                sx={{ bgcolor: '#0d47a1', color: 'rgba(255,255,255,0.7)', textAlign: 'center', py: 3, mt: 4 }}
            >
                <Typography variant="body2">
                    <strong style={{ color: 'white' }}>Gestão AppCodesh</strong> — Sistema ERP SaaS Multi-Tenant
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                    Cadastros · PDV Easy · Delivery · Agenda · Fiscal · Financeiro · Estoque · Academia · Inteligência
                </Typography>
            </Box>

            {/* Botão voltar ao topo */}
            <Fade in={showTop}>
                <Tooltip title="Voltar ao topo">
                    <IconButton
                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                        sx={{
                            position: 'fixed', bottom: 24, right: 24,
                            bgcolor: 'primary.main', color: 'white',
                            boxShadow: 4, zIndex: 999,
                            '&:hover': { bgcolor: 'primary.dark' },
                        }}
                    >
                        <ArrowUpwardIcon />
                    </IconButton>
                </Tooltip>
            </Fade>
        </Box>
    );
}
